// Zählen am Telefon: die Zählliste einer Inventur (Lagerplätze, Nachzählaufträge) und das Einreichen eines Platzes.
// Bei einer Blindzählung enthält KEINE Ausgabe dieser Verträge einen Buchbestand – weder Mengen noch erwartete Artikel.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { COUNT_STATUSES, ENTER, zCountQty, zDay, zItemRef, zLocationRef, zNote, zPerson, zProgress } from './common'

export const ENTRY_MODES = ['bin', 'recount'] as const
export type EntryMode = (typeof ENTRY_MODES)[number]

export const zSheetBin = z.object({
  id: zId,
  code: z.string(),
  name: z.string().nullable(),
  zone: z.string(),
  status: z.enum(['open', 'counted']),
  /** wie oft der Platz gezählt wurde – als `baseRound` mit der Zählung zurückgeben */
  round: z.int(),
  emptyConfirmed: z.boolean(),
  countedBy: zPerson.nullable(),
  countedAt: z.date().nullable(),
  assignedTo: zPerson.nullable(),
  /** dem Nutzer zugeteilt oder frei */
  mine: z.boolean(),
  recountsOpen: z.int(),
  /** nur ohne Blindzählung: was laut Buchbestand beim Start auf dem Platz liegt */
  expected: z
    .array(z.object({ item: zItemRef, lotNo: z.string(), bestBefore: z.string().nullable(), bookQty: z.int() }))
    .optional(),
})

export const zSheetRecount = z.object({
  lineId: zId,
  binId: zId,
  binCode: z.string(),
  item: zItemRef,
  lotNo: z.string(),
  bestBefore: z.string().nullable(),
  assignedTo: zPerson.nullable(),
  note: z.string().nullable(),
  /** der Nutzer darf diese Nachzählung übernehmen (er hat die Position nicht selbst gezählt) */
  mine: z.boolean(),
  /** sonst: warum nicht */
  blockedReason: z.string().nullable(),
})

export const countSheet = defineContract({
  name: 'stocktaking.count.sheet',
  title: 'Zählliste öffnen',
  description: 'Die Zählliste einer laufenden Inventur: Lagerplätze mit ihrem Stand und offene Nachzählungen.',
  llm: {
    description:
      'Liefert die Zählliste einer laufenden Inventur für die Zählenden: Lagerplätze (offen oder gezählt, von wem), ' +
      'offene Nachzählaufträge und den Fortschritt. Bei einer Blindzählung ohne jeden Buchbestand. Nur zum Lesen – ' +
      'Zählmengen erfasst ausschließlich ein Mensch in der Zähloberfläche.',
    keywords: ['Zählliste', 'zählen', 'Lagerplätze', 'Nachzählung'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    name: z.string(),
    status: z.enum(COUNT_STATUSES),
    statusLabel: z.string(),
    location: zLocationRef,
    keyDate: z.string(),
    blind: z.boolean(),
    me: zPerson,
    bins: z.array(zSheetBin),
    recounts: z.array(zSheetRecount),
    progress: zProgress,
  }),
  permission: ENTER,
  risk: 'read',
  scope: 'record',
})

export const zEntryLine = z.strictObject({
  itemId: zId,
  /** Gebinde zu `packQty` (item_unit.id); ohne Angabe das Lagergebinde des Artikels */
  unitId: zId.nullish(),
  /** volle Gebinde in Tausendstel des Gebindes (46 Kästen = 46000) */
  packQty: zCountQty.default(0),
  /** lose Basiseinheiten in Tausendstel (12 Flaschen = 12000) */
  looseQty: zCountQty.default(0),
  lotNo: z.string().trim().max(80).nullish(),
  bestBefore: zDay.nullish(),
  note: zNote,
})

export const countEnter = defineContract({
  name: 'stocktaking.count.enter',
  title: 'Zählung einreichen',
  description: 'Reicht die Zählung eines Lagerplatzes oder eine Nachzählung ein.',
  llm: {
    description:
      'Reicht die Zählung EINES Lagerplatzes ein (`mode: bin`: alles, was dort liegt – was fehlt, gilt als null; ' +
      '`empty` bestätigt einen leeren Platz) oder eine angeordnete Nachzählung einzelner Positionen (`mode: recount`). ' +
      'Mengen: `packQty` volle Gebinde in Tausendstel des Gebindes plus `looseQty` lose Basiseinheiten in Tausendstel. ' +
      'Zählmengen erfasst ausschließlich ein Mensch am Lagerplatz – nie der Assistent.',
    keywords: ['zählen', 'Zählung einreichen', 'Platz leer', 'Nachzählung'],
  },
  input: z.strictObject({
    id: zId,
    binId: zId,
    /** vom Gerät vergebene Kennung dieser Zählliste: Wird sie ein zweites Mal gesendet, ändert sich nichts */
    submissionId: zId,
    mode: z.enum(ENTRY_MODES).default('bin'),
    /** `round` des Platzes beim Öffnen (0 = noch nicht gezählt): Hat inzwischen jemand den Platz gezählt, meldet der Server den Konflikt */
    baseRound: z.int().min(0).default(0),
    /** „Platz leer“ ausdrücklich bestätigt (nur `mode: bin`, ohne Zeilen) */
    empty: z.boolean().default(false),
    /** wann gezählt wurde (Gerätezeit) – für Zählungen, die erst später übertragen werden */
    countedAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }).optional(),
    /**
     * wann das Gerät die Liste sendet (dieselbe Gerätezeit wie `countedAt`): Aus beiden ergibt sich, wie lange die
     * Zählung im Gerät lag – unabhängig davon, ob die Uhr des Geräts richtig geht
     */
    sentAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }).optional(),
    lines: z.array(zEntryLine).max(300, { error: 'Höchstens 300 Positionen je Platz.' }).default([]),
    note: zNote,
  }),
  output: z.object({
    /** diese Zählliste war schon angekommen – es wurde nichts doppelt geschrieben */
    duplicate: z.boolean(),
    /** laufende Nummer der Zählliste in dieser Inventur */
    listNo: z.int(),
    bin: z.object({ id: zId, code: z.string(), status: z.enum(['open', 'counted']), round: z.int() }),
    status: z.enum(COUNT_STATUSES),
    statusLabel: z.string(),
    savedLines: z.int(),
    progress: zProgress,
    message: z.string(),
  }),
  permission: ENTER,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
  limits: { maxItems: 300, itemCount: (input) => input.lines.length },
})
